import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UserPlus, Edit, Key, Trash2, Loader2 } from 'lucide-react';
import { format } from 'date-fns';

export function CreateUserDialog({ open, onOpenChange, form, setForm, onSubmit, isSubmitting }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-amber-400" />
            Create New User
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Add a new substitute teacher or school administrator
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-slate-300">Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm(prev => ({ ...prev, role: v }))}>
              <SelectTrigger className="bg-slate-700 border-slate-600">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="toc">Substitute Teacher</SelectItem>
                <SelectItem value="employer">School Administrator</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Name</Label>
            <Input 
              value={form.name} 
              onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
              className="bg-slate-700 border-slate-600"
              required
              data-testid="create-user-name"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Email</Label>
            <Input 
              type="email"
              value={form.email} 
              onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
              className="bg-slate-700 border-slate-600"
              required
              data-testid="create-user-email"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Password</Label>
            <Input 
              type="password"
              value={form.password} 
              onChange={(e) => setForm(prev => ({ ...prev, password: e.target.value }))}
              className="bg-slate-700 border-slate-600"
              required
              data-testid="create-user-password"
            />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={isSubmitting} className="bg-amber-600 hover:bg-amber-700" data-testid="submit-create-user">
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Create User
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function EditUserDialog({ open, onOpenChange, form, setForm, onSubmit, isSubmitting }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="h-5 w-5 text-amber-400" />
            Edit User
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-slate-300">Name</Label>
            <Input 
              value={form.name || ''} 
              onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
              className="bg-slate-700 border-slate-600"
              data-testid="edit-user-name"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Email</Label>
            <Input 
              type="email"
              value={form.email || ''} 
              onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
              className="bg-slate-700 border-slate-600"
              data-testid="edit-user-email"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Phone</Label>
            <Input 
              value={form.phone || ''} 
              onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))}
              className="bg-slate-700 border-slate-600"
            />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={isSubmitting} className="bg-amber-600 hover:bg-amber-700" data-testid="submit-edit-user">
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ResetPasswordDialog({ open, onOpenChange, newPassword, setNewPassword, onSubmit, isSubmitting }) {
  const handleFormSubmit = (e) => {
    e.preventDefault();
    onSubmit();
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Key className="h-5 w-5 text-amber-400" />
            Reset Password
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Set a new password for this user
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleFormSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-slate-300">New Password</Label>
            <Input 
              type="password"
              value={newPassword} 
              onChange={(e) => setNewPassword(e.target.value)}
              className="bg-slate-700 border-slate-600"
              required
              minLength={6}
              data-testid="new-password-input"
            />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={isSubmitting} className="bg-amber-600 hover:bg-amber-700" data-testid="submit-reset-password">
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Reset Password
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function DeleteUserDialog({ open, onOpenChange, onConfirm, isSubmitting }) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="bg-slate-800 border-slate-700 text-white">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-red-400" />
            Delete User
          </AlertDialogTitle>
          <AlertDialogDescription className="text-slate-400">
            This action cannot be undone. The user will be permanently deleted.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="bg-slate-700 border-slate-600 text-white hover:bg-slate-600">
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction 
            onClick={onConfirm}
            disabled={isSubmitting}
            className="bg-red-600 hover:bg-red-700"
            data-testid="confirm-delete-user"
          >
            {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Delete User
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function UserDetailDialog({ open, onOpenChange, user }) {
  if (!user) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">User Details</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 rounded-full bg-slate-700 flex items-center justify-center overflow-hidden">
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="h-16 w-16 object-cover" />
              ) : (
                <span className="text-2xl text-slate-300">{user.name?.charAt(0)}</span>
              )}
            </div>
            <div>
              <p className="text-xl font-semibold">{user.name}</p>
              <p className="text-slate-400">{user.email}</p>
              <Badge className={user.role === 'toc' ? 'bg-emerald-600 mt-1' : 'bg-blue-600 mt-1'}>
                {user.role === 'toc' ? 'Substitute' : 'School Admin'}
              </Badge>
            </div>
          </div>
          
          {user.role === 'toc' && (
            <div className="space-y-2 bg-slate-700/50 rounded-lg p-4">
              <p className="text-sm text-slate-400">Phone: <span className="text-white">{user.phone || 'Not set'}</span></p>
              <p className="text-sm text-slate-400">Subjects: <span className="text-white">{user.subjects || 'Not set'}</span></p>
              <p className="text-sm text-slate-400">Availability: <span className="text-white">{user.availability || 'Not set'}</span></p>
              <p className="text-sm text-slate-400">Connected Schools: <span className="text-white">{user.school_count || 0}</span></p>
              <p className="text-sm text-slate-400">Positions Accepted: <span className="text-emerald-400">{user.positions_accepted || 0}</span></p>
            </div>
          )}
          
          {user.role === 'employer' && (
            <div className="space-y-2 bg-slate-700/50 rounded-lg p-4">
              <p className="text-sm text-slate-400">Schools Owned: <span className="text-white">{user.schools_owned?.length || 0}</span></p>
              <p className="text-sm text-slate-400">Positions Created: <span className="text-white">{user.positions_created || 0}</span></p>
              {user.schools_owned?.map((school) => (
                <div key={school.school_id} className="flex items-center justify-between bg-slate-800 rounded p-2 mt-2">
                  <span className="text-sm">{school.name}</span>
                  <Badge className="text-xs">{school.archived ? 'Archived' : 'Active'}</Badge>
                </div>
              ))}
            </div>
          )}
          
          <p className="text-xs text-slate-500">
            Registered: {user.created_at ? format(new Date(user.created_at), 'PPP') : 'Unknown'}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
